import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import type { RevealItem } from './reveal-menu.types';

/**
 * The chip row and "Toevoegen" menu of the order form v2 (Figma: stop card "Add ▾", "Driver:" chip).
 * Presentational: the owner decides which fields exist and where they are; this only draws them
 * and reports picks and clears.
 */
@Component({
  selector: 'dd-reveal-menu',
  template: `
    @if (items().length) {
      <div class="dd-reveal-row" data-cy="reveal-row">
        @for (item of chips(); track item.key) {
          <span class="dd-reveal-chip" [attr.data-cy]="'reveal-chip-' + item.key">
            <button type="button" class="dd-reveal-chip__body" (click)="edit.emit(item.key)" [attr.aria-label]="item.editLabel">
              <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:' + item.icon" aria-hidden="true"></mat-icon>
              <span class="dd-reveal-chip__label">{{ item.label }}:</span>
              <span class="dd-reveal-chip__value">{{ item.chipValue }}</span>
              @if (item.extraIcon) {
                <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:' + item.extraIcon" aria-hidden="true" data-cy="reveal-chip-extra-icon"></mat-icon>
              }
            </button>
            <button type="button" class="dd-reveal-chip__clear" (click)="clear.emit(item.key)" [attr.aria-label]="item.label + ' verwijderen'">
              <mat-icon class="icon-size-4" svgIcon="heroicons_outline:x-mark" aria-hidden="true"></mat-icon>
            </button>
          </span>
        }
        <button
          type="button"
          class="dd-reveal-add"
          [matMenuTriggerFor]="menu"
          #trigger="matMenuTrigger"
          [attr.aria-expanded]="trigger.menuOpen"
          data-cy="reveal-add">
          {{ addLabel() }}
          <mat-icon
            class="icon-size-5"
            [svgIcon]="trigger.menuOpen ? 'heroicons_outline:chevron-up' : 'heroicons_outline:chevron-down'"
            aria-hidden="true"></mat-icon>
        </button>
        <mat-menu #menu="matMenu" class="dd-reveal-menu">
          @for (item of items(); track item.key) {
            <button
              mat-menu-item
              type="button"
              [disabled]="item.shown && !item.removable"
              [attr.aria-label]="item.removable ? item.label + ' verwijderen' : null"
              (click)="item.removable ? clear.emit(item.key) : pick.emit(item.key)"
              [attr.data-cy]="'reveal-item-' + item.key">
              <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:' + item.icon"></mat-icon>
              <span>{{ item.label }}</span>
              @if (item.shown) {
                <mat-icon class="icon-size-4 ml-auto !mr-0" svgIcon="heroicons_outline:check" aria-label="Toegevoegd"></mat-icon>
              }
            </button>
          }
        </mat-menu>
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }
    .dd-reveal-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
    }
    /* Figma "Add" button: 32px, Border/Secondary at 1.33px, radius 8, 12px sides. */
    .dd-reveal-add {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      padding: 0 12px;
      border: 1.33px solid var(--dd-border-secondary);
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
    }
    .dd-reveal-add:hover {
      /* Derived from the text colour, so it reads in light and dark without its own token. */
      background: color-mix(in srgb, currentColor 6%, transparent);
    }
    /* Open menu: the trigger stays pressed (Figma ~8% fill, derived from the text colour for light and dark). */
    .dd-reveal-add[aria-expanded='true'] {
      background: color-mix(in srgb, currentColor 8%, transparent);
    }
    /* Figma menu: 216 wide, 8px under the trigger, 36px rows, 16px icons in the text colour, hover row = Border colour at radius 8. */
    ::ng-deep .dd-reveal-menu.mat-mdc-menu-panel {
      width: 216px;
      min-width: 216px;
      max-width: 216px;
      margin-top: 8px;
    }
    ::ng-deep .dd-reveal-menu .mat-mdc-menu-item {
      min-height: 36px;
      border-radius: 8px;
      --mat-menu-item-icon-color: var(--fuse-text-default);
      --mat-menu-item-hover-state-layer-color: transparent;
      --mat-menu-item-focus-state-layer-color: transparent;
    }
    ::ng-deep .dd-reveal-menu .mat-mdc-menu-item:hover:not([disabled]),
    ::ng-deep .dd-reveal-menu .mat-mdc-menu-item.cdk-keyboard-focused:not([disabled]) {
      background: var(--fuse-border);
    }
    /* Rows touch (Figma: 3 x 36 + 7 padding = 122): the base layer makes the menu a flex column with a 4px gap. */
    ::ng-deep .dd-reveal-menu.mat-mdc-menu-panel .mat-mdc-menu-content {
      padding: 7px 0;
      gap: 0;
    }
    /* Specificity matches the fuse "no colour" icon rule, which would otherwise keep the grey. */
    ::ng-deep .dd-reveal-menu.mat-mdc-menu-panel .mat-mdc-menu-content .mat-mdc-menu-item .mat-icon {
      width: 16px;
      height: 16px;
      font-size: 16px;
      color: var(--fuse-text-default);
    }
    .dd-reveal-chip {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      height: 32px;
      border: 1px solid var(--dd-border-default);
      border-radius: 6px;
      background: var(--dd-field-bg, transparent);
    }
    .dd-reveal-chip__body {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
      height: 100%;
      padding: 0 4px 0 8px;
      font-size: 12px;
      line-height: 1.4;
    }
    .dd-reveal-chip__label {
      font-weight: 600;
      white-space: nowrap;
    }
    .dd-reveal-chip__value {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .dd-reveal-chip__clear {
      display: inline-flex;
      align-items: center;
      height: 100%;
      padding: 0 8px 0 4px;
    }
    /* The project's base layer drops the UA focus ring; these are native buttons styled from scratch. */
    .dd-reveal-add:focus-visible,
    .dd-reveal-chip button:focus-visible {
      outline: 2px solid currentColor !important;
      outline-offset: 1px;
    }
  `,
  imports: [MatIconModule, MatMenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevealMenuComponent {
  readonly items = input<RevealItem[]>([]);
  readonly addLabel = input('Toevoegen');
  readonly pick = output<string>();
  readonly clear = output<string>();
  readonly edit = output<string>();

  /** The chip body's accessible name carries what the chip shows: an aria-label replaces the content. */
  protected readonly chips = computed(() =>
    this.items()
      .filter(item => item.chipValue !== null)
      .map(item => {
        const extra = item.extraLabel && item.extraLabel !== item.chipValue ? `, ${item.extraLabel}` : '';
        return { ...item, editLabel: `${item.label}: ${item.chipValue}${extra}, bewerken` };
      })
  );
}
